import React, { useEffect, useImperativeHandle, useState, ForwardRefRenderFunction } from "react";
import Modal from "react-modal";
import { postData, putData, uploadImage } from "../../services/Axios/Axios";
import { ApiUrl } from "../../config/apiConfig";
import { useLoader } from "../../components/loader/loader";
import messageService from "../../services/message/messageService";
import { showAPiError_mesage } from "../../services/validationService/validationservice";
import { ImagePlus, X, XCircle } from "lucide-react";

interface Props {
  isOpen: boolean;
  openModal: any;
  closeModal: () => void;
  getDataALLs?: () => void;
}

const AddPracticeDocumentModal: ForwardRefRenderFunction<any, Props> = (props, ref) => {
  const { isOpen, openModal, closeModal, getDataALLs } = props;
  const { show, hide } = useLoader();
  const [formData, setFormData] = useState({ title: "", type: "", urls: [] as string[], isIFTA: false });
  const [error, setError] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (openModal) {
      setFormData({
        title: openModal?.title || "",
        type: openModal?.type || "",
        urls: openModal?.urls || [],
        isIFTA: openModal?.isIFTA || false
      });
    } else {
      resetForm();
    }
  }, [openModal]);

  useImperativeHandle(ref, () => ({
    resetForm,
  }));

  const resetForm = () => {
    setFormData({ title: "", type: "", urls: [], isIFTA: false });
    setError({});
  };

  const validate = () => {
    const newErrors: any = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    // if (!formData.type.trim()) newErrors.type = "Type is required";
    if (!formData.urls.length) newErrors.urls = "At least one file URL is required";
    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {

    if (!validate() && !formData.isIFTA) return;
    
    try {
      const payload = { ...formData, _id: openModal?._id };
      const url = openModal?._id
        ? ApiUrl.updatePracticeDocument + openModal?._id
        : ApiUrl.createPracticeDocument;

      if (openModal?._id) {

        // in case of update we don't need id 
        delete payload._id;

        await putData(url, payload);
      } else {
        await postData(url, payload, { show, hide });
      }

      const notify = messageService();
      notify.toast("success", openModal?._id ? notify.message.updatedSuccess : notify.message.addedSuccess);

      closeModal();
      resetForm();
      getDataALLs?.();
    } catch (err: any) {
      console.error(err);
      const msg = err?.response?.data?.message || "Something went wrong";
      showAPiError_mesage(msg);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({ ...prev, [name]: "" }));
  };

  // File Upload Handler (reusing uploadImage or your uploader)
  const uploadFile = async (file: File) => {
    try {
      const res = await uploadImage(ApiUrl.fileUpload, { image: file }, { show, hide });
      setFormData((prev) => ({ ...prev, urls: [...prev.urls, res.original] }));
      setError((prev) => ({ ...prev, urls: "" }));
    } catch (err) {
      showAPiError_mesage("Failed to upload file");
    }
  };

  const removeFile = (url: string) => {
    setFormData((prev) => ({ ...prev, urls: prev.urls.filter((u) => u !== url) }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      ariaHideApp={false}
      className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
    >
      <div className="bg-white w-full max-w-xl rounded-md shadow-lg p-6 ease-in-out duration-1000">
        <div className="flex justify-between items-start">
          <h2 className="text-xl font-bold mb-4">
            {openModal?._id ? "Edit Practice Document" : "Add Practice Document"}
          </h2>
          <button onClick={closeModal}><X /></button>
        </div>

        {!formData.isIFTA &&
          <>
            {/* Title */}
            <div className="mb-4">
              <label className="block mb-1 font-medium">1st Document Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full p-2 text-md border border-gray-300 rounded-md hover:border-greenBg focus:border-[1px] focus:border-greenBg/50 outline-none focus:shadow-[0px_0px_8px_rgba(0,112,112,0.6)] transition-all duration-300"
              />
              {error.title && <p className="text-red-500 text-sm">{error.title}</p>}
            </div>

            {/* Type */}
            <div className="mb-4">
              <label className="block mb-1 font-medium">2nd Document Title</label>
              <input
                type="text"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full p-2 text-md border border-gray-300 rounded-md hover:border-greenBg focus:border-[1px] focus:border-greenBg/50 outline-none focus:shadow-[0px_0px_8px_rgba(0,112,112,0.6)] transition-all duration-300"
              />
              {error.type && <p className="text-red-500 text-sm">{error.type}</p>}
            </div>
          </>}

        {/* <div className="flex items-center mb-4">
          <input id="default-checkbox" type="checkbox" checked={formData.isIFTA} onChange={(e) => {
            const checked = e.target.checked;
            setFormData((prev) => ({
              ...prev,
              isIFTA: checked
            }))

          }} className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm  dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600" />
          <label htmlFor="default-checkbox" className="ms-2 text-sm font-medium text-gray-900 ">Is IFTA Document</label>
        </div> */}


        {/* File Upload */}
        <div className="mb-4">
          <label className="block mb-1 font-medium">Upload Documents</label>
          <div className="border border-dashed p-4 text-center rounded-md cursor-pointer bg-gray-50">
            <label htmlFor="fileUpload" className="cursor-pointer text-gray-500">
              <ImagePlus className="mx-auto mb-2" /> Upload File
            </label>
            <input
              type="file"
              id="fileUpload"
              className="hidden"
              onChange={(e) => {

                if(formData.urls.length===2) {
                  showAPiError_mesage("Only 2 Documents are allowed")
                  return
                }

                if (e.target.files?.[0]) uploadFile(e.target.files[0]);
              }}
            />
          </div>

          {/* Preview Uploaded Files */}
          <div className="mt-3 space-y-2">
            {formData.urls.map((url, idx) => (
              <div key={idx} className="flex items-center justify-between border rounded p-2">
                <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline truncate max-w-xs">
                  {url}
                </a>
                <button onClick={() => removeFile(url)} className="ml-2">
                  <XCircle className="text-red-500" />
                </button>
              </div>
            ))}
          </div>
          {error.urls && <p className="text-red-500 text-sm">{error.urls}</p>}
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-customOrange text-white rounded-md hover:bg-greenBg text-sm font-semibold shadow"
          >
            {openModal?._id ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default React.forwardRef(AddPracticeDocumentModal);
